import React, { useEffect, useState } from 'react';
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import { isTestEnv } from 'common/report/util/env';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { DropMenuReact } from 'common/components/drop-menu';
import { Directions } from 'common/components/drop-menu/types';
import { Visibility } from 'common/components/base/stackable';
import { ActionEvent, BannerAction, BannerTheme, BannerType } from 'common/components/banner/types';
import { BannerReact } from 'common/components/banner';
import { useTranslation } from 'common/i18n/hooks';
import { TranslationKey } from 'common/i18n';
import { hasFirstClassMetrics } from 'apps/lexicon/src/lexicon-app/metrics/utils';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { withStyles } from 'common/util/react';
import { promptFeedbackModal } from 'common/widgets/feedback-modal';
import { FeedbackForFeature } from 'common/widgets/feedback-modal/types';
import { useLocalStorageManager } from 'common/contexts/react/storage-manager-context';
import { RestrictEntitySaving } from 'common/report/constants';
import { reporter } from 'common/report/error-reporting';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { getPersistedState, setPersistedState } from './util';

// [METRICSGA] Delete this whole component and everything added in this PR once
// First Class Metrics is GA.

const PRE_BETA_LEARN_MORE = "https://philosophical-champion-811384.framer.app/saved-metrics";
const BETA_LEARN_MORE = "https://philosophical-champion-811384.framer.app/saved-metrics-hub";
const BETA_WALKTHROUGH_URL = "https://philosophical-champion-811384.framer.app/learning";
type BannerOptions = {
  button: TranslationKey;
  ctaText: TranslationKey | null;
  description: TranslationKey;
  secondaryCtaText: TranslationKey;
  title: TranslationKey;
};
const preBetaBannerOptions: BannerOptions = {
  button: "first-class-metrics-info.pre-beta.button",
  ctaText: "first-class-metrics-info.pre-beta.cta",
  description: "first-class-metrics-info.pre-beta.description",
  secondaryCtaText: "first-class-metrics-info.pre-beta.secondary-cta",
  title: "first-class-metrics-info.pre-beta.title"
};
const preBetaRaisedHandBannerOptions: BannerOptions = {
  button: "first-class-metrics-info.pre-beta.button",
  ctaText: null,
  description: "first-class-metrics-info.pre-beta-raised-hand.description",
  secondaryCtaText: "first-class-metrics-info.pre-beta.secondary-cta",
  title: "first-class-metrics-info.pre-beta.title"
};
const betaBannerOptions: BannerOptions = {
  button: "first-class-metrics-info.beta.button",
  ctaText: "first-class-metrics-info.beta.cta",
  description: "first-class-metrics-info.beta.description",
  secondaryCtaText: "first-class-metrics-info.beta.secondary-cta",
  title: "first-class-metrics-info.beta.title"
};
const MetricsBetaButton = () => {
  var _currentProjectResour;
  if (isTestEnv()) {
    return null;
  }
  const [metricsPopupOpen, setMetricsPopupOpen] = useState(false);
  const {
    t
  } = useTranslation();
  const currentProjectResource = useCurrentProject();
  const localStorageManager = useLocalStorageManager();
  const isBeta = hasFirstClassMetrics(currentProjectResource);
  const persistedState = getPersistedState({
    localStorageManager,
    projectId: currentProjectResource.id
  });
  const isRaisedHand = persistedState.raisedHandPreBeta;
  let bannerOptions: BannerOptions = betaBannerOptions;
  if (!isBeta) {
    if (isRaisedHand) {
      bannerOptions = preBetaRaisedHandBannerOptions;
    } else {
      bannerOptions = preBetaBannerOptions;
    }
  }

  // If the user hasn't already closed the popup, open it.
  useEffect(() => {
    if ((!persistedState.closedBeta && isBeta || !persistedState.closedPreBeta && !isBeta) && !metricsPopupOpen) {
      setMetricsPopupOpen(true);
    }
  }, [isBeta, metricsPopupOpen, persistedState.closedBeta, persistedState.closedPreBeta]);

  // If the user doesn't have access to behaviors metrics, don't show the button at all.
  const hasBehaviorsMetricsAccess = Boolean((_currentProjectResour = currentProjectResource.get()) === null || _currentProjectResour === void 0 || (_currentProjectResour = _currentProjectResour.value) === null || _currentProjectResour === void 0 ? void 0 : _currentProjectResour.canSaveEntity(RestrictEntitySaving.BehaviorMetrics));
  if (isTestEnv() || !hasBehaviorsMetricsAccess) {
    return null;
  }
  const closePopup = () => {
    setMetricsPopupOpen(false);
    try {
      const updatedState = produce(persistedState, draft => {
        if (isBeta) {
          draft.closedBeta = true;
        } else {
          draft.closedPreBeta = true;
        }
        return draft;
      });
      if (!isEqual(persistedState, updatedState)) {
        setPersistedState({
          localStorageManager,
          updatedState
        });
      }
    } catch (error) {
      reporter.error(error);
    }
  };
  const onRaiseHand = () => {
    const updatedState = produce(persistedState, draft => {
      draft.raisedHandPreBeta = true;
      return draft;
    });
    if (!isEqual(persistedState, updatedState)) {
      setPersistedState({
        localStorageManager,
        updatedState
      });
    }
  };
  return <div data-sentry-component="MetricsBetaButton" data-sentry-source-file="index.tsx">
      <DropMenuReact className={styles.dropMenu} direction={Directions.UpCenter} experimentalPositioning open={metricsPopupOpen} onChange={ev => "detail" in ev && ev.detail.state === Visibility.Closed && closePopup()} data-sentry-element="DropMenuReact" data-sentry-source-file="index.tsx">
        <BannerReact bodyText={t(bannerOptions.description)} ctas={[...(bannerOptions.ctaText ? [{
        actionName: BannerAction.Custom,
        text: t(bannerOptions.ctaText),
        theme: ButtonTheme.Primary
      }] : []), {
        actionName: BannerAction.Help,
        text: t(bannerOptions.secondaryCtaText),
        theme: ButtonTheme.Tertiary
      }]} headerText={t(bannerOptions.title)} icon='behavior-simple' onAction={async (ev: ActionEvent) => {
        const actionName = ev.detail.actionName;
        if (actionName === BannerAction.Dismiss) {
          closePopup();
        } else if (actionName === BannerAction.Help) {
          window.open(isBeta ? BETA_LEARN_MORE : PRE_BETA_LEARN_MORE, "_blank");
        } else if (actionName === BannerAction.Custom) {
          // Pre-beta users open the feedback modal to say they want to join
          // the beta.
          if (!isBeta) {
            const [submitted] = await promptFeedbackModal({
              feature: FeedbackForFeature.FeatureRequest
            }, {
              feedbackText: _ref => {
                let {
                  t
                } = _ref;
                return t("first-class-metrics-info.feedback-text");
              }
            });
            if (submitted) {
              onRaiseHand();
            }
          } else {
            // Beta users get sent to the Framer walkthrough.
            window.open(BETA_WALKTHROUGH_URL, "_blank");
          }
        }
      }} showCloseButton theme={BannerTheme.BaseColumn} type={BannerType.Embedded} data-sentry-element="BannerReact" data-sentry-source-file="index.tsx" />
      </DropMenuReact>

      <ButtonReact theme={ButtonTheme.Tertiary} notFocusable onClick={() => !metricsPopupOpen && setMetricsPopupOpen(true)} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
        {t(bannerOptions.button)}
      </ButtonReact>
    </div>;
};
export const MetricsBetaButtonReact = withStyles(MetricsBetaButton, css);